<template>
  <v-app>
    <!-- 內嵌在社群、AI 站對話框裡的登入畫面（views/EmbedLogin.vue）只顯示表單，全站的提示視窗都不要 -->
    <BrowserGuard v-if="!isEmbed" />
    <ExternalLinkDialog />
    <PushPermissionDialog v-if="enableNotifications && !isEmbed" />

    <div v-if="isTestEnv && !isEmbed" class="watermark">測 試 資 料</div>

    <v-main :class="$store.state.isSerivceRouter ? '' : 'main-bg-img'">
      <GlobalAgentNotification v-if="!isEmbed" />
      <router-view :key="$store.state.routerViewKey" />
      <!-- 手機 App 模式的底部導覽列：登入後、五個分頁本身才出現（見 utils/appTabBar.js） -->
      <AppTabBar v-if="!isEmbed" />

      <!-- 原始訊息提示 -->
      <v-snackbar v-model="snackbar.show" :timeout="snackbar.timeout" top>
        <v-icon small dark :color="snackbar.status" class="mr-2">
          {{ snackbar.status === "success" ? "mdi-check-circle" : "mdi-alert-circle" }}
        </v-icon>
        {{ snackbar.content }}
        <template #action="{ attrs }">
          <v-btn small v-bind="attrs" color="primary" @click="snackbar.show = false">
            {{ snackbar.close || "關閉" }}
          </v-btn>
        </template>
      </v-snackbar>
    </v-main>
    <LoginDialog v-if="!isEmbed" />
    <AccountSwitchDialog v-if="!isEmbed" />
    <!-- 帳號還在刪除冷靜期：登入後提醒，可以一鍵取消 -->
    <DeletionPendingNotice v-if="!isEmbed" />
  </v-app>
</template>

<script>
import ExternalLinkDialog from "@/components/ExternalLinkDialog.vue";
import GlobalAgentNotification from "@/components/Common/GlobalAgentNotification.vue";
import PushPermissionDialog from "@/components/Common/PushPermissionDialog.vue";
import LoginDialog from "@/components/Common/LoginDialog.vue";
import AccountSwitchDialog from "@/components/Common/AccountSwitchDialog.vue";
import AppTabBar from "@/components/Common/AppTabBar.vue";
import DeletionPendingNotice from "@/components/Common/DeletionPendingNotice.vue";
import { SplashScreen } from "@capacitor/splash-screen";
import { ENABLE_NOTIFICATIONS } from "@/config/features";
import { setNotifyRoot } from "@/plugins/notify";

export default {
  components: { ExternalLinkDialog, GlobalAgentNotification, PushPermissionDialog, LoginDialog, AccountSwitchDialog, AppTabBar, DeletionPendingNotice },
  name: "App",
  metaInfo: {
    link: [
      { vmid: 'favicon', rel: 'icon', type: 'image/png', href: '/img/icons/favicon-32x32.png' }
    ]
  },
  data() {
    return {
      serivces: {
        sms: process.env.VUE_APP_MAIN_SMS_BASE_URL,
        wt: process.env.VUE_APP_MAIN_WT_BASE_URL,
        aichat: process.env.VUE_APP_MAIN_AICHAT_BASE_URL + "/listener",
      },
      snackbar: {
        show: false,
        content: "",
        status: "",
        timeout: 3000,
        close: "關閉",
      },
    };
  },
  watch: {
    // 登入狀態變化 → 啟動/停止通知未讀數輪詢
    "$store.state.signinStatus": {
      immediate: true,
      handler(v) {
        if (!ENABLE_NOTIFICATIONS) return; // 通知功能暫未上正式
        if (v) this.$store.dispatch("notifications/startPolling");
        else this.$store.dispatch("notifications/stopPolling");
      },
    },
  },
  created() {
    // 自訂頁面短碼的 $notify 也要送到這裡（見 plugins/notify.js）
    setNotifyRoot(this.$root);
    this.$root.$on("show-snackbar", (payload) => {
      this.snackbar = {
        ...payload,
        show: false,
      };
      this.$nextTick(() => {
        this.snackbar.show = true;
      });
    });

    // SSO: 初始化會話 (嘗試靜默登入)
    this.initSession();
    this.initSyncAuth();
  },
  mounted() {
    // 監聽使用者任何操作，觸發 SW update check
    this._swCheckOnInteraction = () => {
      if (typeof window.__swCheckUpdate === "function") {
        window.__swCheckUpdate();
      }
    };
    window.addEventListener("click", this._swCheckOnInteraction, true);
    window.addEventListener("keydown", this._swCheckOnInteraction, true);
    window.addEventListener("touchstart", this._swCheckOnInteraction, true);

    // 開機載入畫面(紫色轉圈)改由 router.onReady 在「初始路由真正就緒」後收掉，
    // 避免 App 剛掛載但頁面 chunk／靜默登入尚未完成就假性收掉。
    // 這裡只負責保底隱藏原生啟動圖。
    this.$nextTick(() => {
      try {
        SplashScreen.hide();
      } catch (e) {
        /* web 或未啟用時忽略 */
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener("click", this._swCheckOnInteraction, true);
    window.removeEventListener("keydown", this._swCheckOnInteraction, true);
    window.removeEventListener("touchstart", this._swCheckOnInteraction, true);
  },
  methods: {
    async syncAuthState() {
      await this.$store.dispatch("GetEmailEncrypt");
      if (!this.$store.state.signinStatus) {
        await this.$store.dispatch("SilentSignIn");
      }
    },
    /**
     * 初始化 Session：若無 Token 則嘗試靜默登入
     */
    async initSession() {
      // 統一由 router.beforeEach 處理靜默登入
      // 這裡只需要監聽狀態即可，或者暫時留空
      console.log("[App] Session initialization handled by router.");
    },
    initSyncAuth() {
      // 監聽頁面可見性變化，當使用者回到此分頁時主動同步全站登入狀態
      let lastCheck = 0;
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          const now = Date.now();
          if (now - lastCheck > 10000) { // 10秒內不重複檢查
            lastCheck = now;
            console.log('[SSO] Page focused, syncing auth state...');
            this.syncAuthState();
          }
        }
      });
    },
  },

  computed: {
    isEmbed() {
      return !!(this.$route.meta && this.$route.meta.embed);
    },
    isTestEnv() {
      // 依據環境變數決定是否為測試環境
      return process.env.VUE_APP_PUBLIC_ENV === "preview";
    },
    enableNotifications() {
      return ENABLE_NOTIFICATIONS;
    },
  },
};
</script>

<style lang="sass">
@import '~vuetify/src/styles/styles.sass'


@media #{map-get($display-breakpoints, 'md-and-down')}
    .custom-class
        display: none
</style>

<style>
.watermark {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%; /* 佔滿寬度 */
  height: 100%; /* 佔滿高度 */
  display: flex;
  justify-content: center;
  align-items: center;
  transform: rotate(-30deg);
  font-size: 16vw; /* 使用 vw 單位，隨螢幕寬度縮放 */

  color: rgba(255, 0, 0, 0.1);
  pointer-events: none;
  z-index: 9999;
}

/* 針對不同裝置進行微調 */
@media (max-width: 768px) {
  .watermark {
    font-size: 20vw;
  }
}

@media (max-width: 480px) {
  .watermark {
    font-size: 21vw;
    font-weight: 700;
  }
}
</style>
